import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import {
    CellType,
    IColumnMovingEventArgs,
    IGridState,
    IgxDialogComponent,
    IgxGridStateDirective,
    IgxHierarchicalGridComponent,
    ISelectionEventArgs,
    RowType
} from '@infragistics/igniteui-angular';
import { GridType } from '@infragistics/igniteui-angular/lib/grids/common/grid.interface';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from 'src/app/authentication/user.service';
import { BusStop } from 'src/app/models/bus-stop';
import { GridState } from 'src/app/models/grid-state';
import { Kid, KidForm } from 'src/app/models/kid';
import { KidSport, KidSportForm } from 'src/app/models/kid_sport';
import { Level } from 'src/app/models/level';
import { Parent } from 'src/app/models/parent';
import { Sport } from 'src/app/models/sport';
import { KidService } from 'src/app/services/kid.service';
import { MessagesService } from 'src/app/services/messages.service';
import { WaitIndicatorService } from 'src/app/services/wait-indicator.service';
import { restoreState } from 'src/app/shared/utils';
import { AddPackageComponent } from '../add-package/add-package.component';
import { EditKidFormComponent } from '../edit-kid-form/edit-kid-form.component';
import { InfoKidComponent } from '../info-kid/info-kid.component';
import { Gender } from 'src/app/models/enums/gender';

@Component({
    selector: 'app-edit-kids',
    templateUrl: './edit-kids.component.html',
    styleUrls: ['./edit-kids.component.scss']
})
export class EditKidsComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('kidsGrid', { static: true, read: IgxHierarchicalGridComponent })
    private kidsGrid!: IgxHierarchicalGridComponent;
    @ViewChild(IgxGridStateDirective, { static: true })
    private state!: IgxGridStateDirective;
    @ViewChild('dialog', { static: true, read: IgxDialogComponent })
    private dialog?: IgxDialogComponent;
    @ViewChild('sportDialog', { static: true, read: IgxDialogComponent })
    private sportDialog?: IgxDialogComponent;
    @ViewChild('addProgram', { static: true, read: IgxDialogComponent })
    private addProgram?: IgxDialogComponent;
    @ViewChild('addPackageDialog', { static: true, read: AddPackageComponent })
    private addPackageDialog!: AddPackageComponent;
    @ViewChild('infoKidDialog', { static: true, read: InfoKidComponent })
    private infoKidDialog!: InfoKidComponent;

    private destroy$: Subject<void> = new Subject<void>();
    private gridName: string = 'edit-kids-grid';
    private gridState?: GridState;

    public kids: Kid[] = [];
    public parents: Parent[] = [];
    public stops: BusStop[] = [];
    public sports: Sport[] = [];
    public levels: Level[] = [];
    public kidSports: KidSport[] = [];
    public customOverlaySettings = {};
    public formatDateOptions = { format: 'dd/MM/yyyy', timezone: 'GMT+0200' };
    public kidForm: FormGroup<KidForm>;
    public kidSportForm: FormGroup<KidSportForm>;
    public editKidSport: KidSport;
    public allDataLoaded = false;
    public dialogText = '';

    constructor(
        private kidService: KidService,
        private userService: UserService,
        private msgs: MessagesService,
        private wait: WaitIndicatorService,
        router: Router
    ) {
        this.kidForm = new FormGroup<KidForm>({
            name: new FormControl('', Validators.required),
            birthDate: new FormControl(null),
            gender: new FormControl(null),
            footSize: new FormControl(null),
            height: new FormControl(null),
            hasDeclaration: new FormControl(null),
            allowShooting: new FormControl(null),
            notes: new FormControl(null),
            friends: new FormControl([]),
            siblings: new FormControl([]),
            egn: new FormControl(null),
            parents: new FormControl([]),
            morningStop: new FormControl(null),
            eveningStop: new FormControl(null),
        });
        this.kidSportForm = new FormGroup<KidSportForm>({
            sport: new FormControl(null),
            level: new FormControl(null)
        });

        this.wait.show();
        this.kidService.all().then(data => {
            this.kids = data.kids;
            this.parents = data.parents;
            this.stops = data.busStops;
            this.kidSports = data.kidSports;
            this.sports = data.sports;
            this.wait.hide();
        });
        this.allDataLoaded = true;
        this.editKidSport = this._emptyKidSport();
        router.events.pipe(takeUntil(this.destroy$)).subscribe(e => {
            if (e instanceof NavigationStart) {
                if (this.gridState) {
                    this.gridState.options = this.state.getState().toString();
                    this.gridState.userId = this.userService.currentUser!.id!;
                } else {
                    this.gridState = {
                        gridName: this.gridName,
                        options: this.state.getState().toString(),
                        userId: this.userService.currentUser!.id!,
                    };
                }
                this.userService.updateGridState(this.gridState);
            }
        });
    }

    ngOnInit(): void {
        this.userService.gridStates().pipe(takeUntil(this.destroy$)).subscribe((states: GridState[]) => {
            const loaded = states.filter(s => s.gridName === this.gridName && s.userId === this.userService.currentUser!.id!)[0];
            if (loaded) {
                this.gridState = loaded;
                const gridState: IGridState = JSON.parse(this.gridState?.options ?? '');
                restoreState(gridState, this.kidsGrid);
            }
        });
        this.addPackageDialog.done.pipe(takeUntil(this.destroy$)).subscribe(_ => this.addProgram?.close())
    }

    ngAfterViewInit(): void {
        this.customOverlaySettings = { outlet: this.kidsGrid?.outlet };
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public parseKidsArray = (arr?: Kid[]): string => {
        return (arr || []).map(e => this.kids.find(k => k.id === e.id)?.name).join(', ');
    }

    public parseParentsArray = (arr?: Parent[]): string => {
        return (arr || []).map(e => e?.name).join(', ');
    }

    public parseStop(item: CellType | BusStop | string): string {
        let stopId: string | undefined;
        let result = '';
        const busStop = item as BusStop;
        if (busStop) {
            stopId = busStop.id;
        } else if (item.hasOwnProperty('grid')) {
            stopId = (item as CellType).value?.id;
        } else {
            stopId = item as string;
        }
        const stop = this.stops.find(s => s.id === stopId);
        result = stop?.name ?? '';
        return result;
    }

    public addKid = (form: EditKidFormComponent, dialog: IgxDialogComponent): void => {
        this.dialogText = 'Добави';
        form.kids = this.kids;
        form.parents = this.parents;
        form.stops = this.stops;
        form.kid = undefined;
        dialog.open();
    }

    public updateKid = (form: EditKidFormComponent, dialog: IgxDialogComponent, cell: CellType): void => {
        const kid = this.kids.find(k => k.id === cell.row.data.id);
        if (!kid) return;

        form.kids = this.kids;
        form.parents = this.parents;
        form.stops = this.stops;
        form.kid = kid;
        this.dialogText = 'Редактирай';
        dialog.open();
    }

    public addKidDone = (kid: Kid | undefined, form: EditKidFormComponent, dialog: IgxDialogComponent) => {
        if (kid?.morningStop && typeof kid.morningStop === 'string') {
            kid.morningStop = this.stops.find(s => s.id === kid.morningStop);
        }
        if (kid?.eveningStop && typeof kid.eveningStop === 'string') {
            kid.eveningStop = this.stops.find(s => s.id === kid.eveningStop);
        }
        if (kid && form.dialogText === 'Добави') this.kids = [...this.kids, kid];
        else if (kid) {
            const index = this.kids.findIndex(k => k.id === kid.id);
            this.kids.splice(index, 1, kid);
            this.kids = [...this.kids];
        }
        dialog.close();
    }

    public addKidSport(rowIsland: GridType, sportDialog: IgxDialogComponent) {
        const kidId = rowIsland.childRow.data.rowID;
        const kid = this.kids.find(k => k.id === kidId);
        this.editKidSport.kid = kid;
        this.dialogText = 'Добави';
        sportDialog.open();
    }

    public updateKidSport = async (cell: CellType): Promise<void> => {
        this.editKidSport = this.kidSports.find(ks => ks.id === cell.row.data.id)!;
        this.editKidSport.kid = this.kids.find(k => k.id === this.editKidSport.kid);
        this.editKidSport.sport = this.sports.find(s => s.id === this.editKidSport.sport?.id);
        this.levels = this.editKidSport.sport?.levels ?? [];
        this.editKidSport.level = this.levels.find(l => l.id === this.editKidSport.level?.id);
        this.kidSportForm.reset(this.editKidSport);
        this.dialogText = 'Редактирай';
        this.sportDialog?.open();
    }

    public deleteKidSport = async (row: RowType) => {
        const kidSport = await this.kidSports.find(ks => ks.id === row.key);
        const kidId = kidSport?.kid;
        try {
            const result = await this.kidService.deleteSport(kidSport?.id);
            if (result) {
                this.msgs.success(`Спортът е успешно изтрит.`);
                const kid = this.kids.find(k => k.id === kidId);
                let index = kid?.sports.findIndex(s => s.id === row.key)!;
                kid?.sports.splice(index, 1);
                index = this.kidSports.findIndex(ks => ks.id === kidSport?.id);
                this.kidSports.splice(index, 1);
                this.kidSports = [... this.kidSports];
                this.kidsGrid?.collapseRow(kid?.id);
                this.kidsGrid?.expandRow(kid?.id);
            }
        }
        catch (err: any) {
            this.msgs.error(err);
        }
    }

    public closeSportDialog(dialog: IgxDialogComponent) {
        this.kidSportForm.reset(this._emptyKidSport());
        dialog.close();
    }

    public kidSportSubmit = async () => {
        const data = {
            id: '',
            kidId: this.editKidSport.kid?.id,
            sportId: this.kidSportForm.value.sport?.id,
            levelId: this.kidSportForm.value.level?.id,
        };
        try {
            const isAddRow = this.dialogText === 'Добави';
            const message = isAddRow ? 'добавен' : 'обновен';
            let result: KidSport;
            let kid: Kid | undefined;
            if (isAddRow) {
                result = await this.kidService.addSport(data);
                this.kidSports = [... this.kidSports, result];
                kid = this.kids.find(k => k.id === result.kid);
                kid?.sports.push(result);
            } else {
                data.id = this.editKidSport.id!;
                result = await this.kidService.updateSport(data);
                let index: number = this.kidSports.findIndex(ks => ks.id === result.id);
                this.kidSports.splice(index, 1, result);
                kid = this.kids.find(k => k.id === result.kid);
                index = kid!.sports.findIndex(s => s.id === result.id);
                kid!.sports.splice(index, 1, result);
            }
            result.sport = this.sports.find(s => s.id === result.sport);
            result.level = this.levels.find(l => l.id === result.level);
            this.msgs.success(`Спортът ${result.sport!.name}-${result.level?.name ? result.level.name : 'НЯМА НИВО'} е успешно ${message}.`);

            this.kidSportForm.reset(this._emptyKidSport());
            this.editKidSport = this._emptyKidSport();
            this.kidsGrid?.collapseRow(kid!.id);
            this.kidsGrid?.expandRow(kid!.id);
            this.sportDialog?.close();
        }
        catch (err: any) {
            this.msgs.error(err);
        }
    }

    public sportSelectChanging = async (e: ISelectionEventArgs): Promise<void> => {
        const newSport: Sport = e.newSelection.value;
        this.levels = newSport.levels;
        this.kidSportForm.controls.level.reset();
    }

    public addPackage = async (cell: CellType, dialog: IgxDialogComponent) => {
        const kid = this.kids.find(k => k.id === cell.row.key);
        await this.addPackageDialog.loadData();
        this.addPackageDialog.clear();
        this.addPackageDialog.kid = kid;
        dialog.open();
    }

    public getInfo = (cell: CellType, dialog: IgxDialogComponent): void => {
        this.infoKidDialog.kid = cell.row.data as Kid;
        dialog.open();
    }

    protected columnMoving = (e: IColumnMovingEventArgs): void => {
        e.cancel = e.source.pinned;
    }

    private _emptyKidSport = (): KidSport => {
        return {
            kid: this._emptyKid(),
            sport: this._emptySport(),
            level: this._emptyLevel(),
        }
    };
    private _emptyKid = (): Kid => {
        return {
            name: '',
            gender: Gender.Male,
            notes: '',
            friends: [],
            siblings: [],
            sports: [],
            parents: [],
            packages: [],
        }
    };
    private _emptySport = (): Sport => { return { name: '', levels: [], periods: [] } };
    private _emptyLevel = (): Level => { return { name: 'Изберете спорт', sport: undefined!, description: '', public: false, periods: [] } };
}
